import { Constants } from "@socialcoach/services";
import { scCatchServiceException } from "@/annotations";
import {
  Page,
  SmartLink,
  SmartLinkExtraParam,
  SmartLinkReportType,
  SmartLinkSearchQuery,
  SmartLinkStats,
} from "@/services/bedrock/dtos";
import axios from "@/services/bedrock/AxiosClient";

const SMART_LINK_SERVICE = `${Constants.API_URL}/bedrock/smart_links`;
const SMART_LINK_PARAMS_SERVICE = `${Constants.API_URL}/bedrock/smart_link_extra_params`;

export class SmartLinkServices {
  @scCatchServiceException()
  static async getSmartLinks(query: SmartLinkSearchQuery): Promise<Page<SmartLink>> {
    const { data } = await axios.get<Page<SmartLink>>(`${SMART_LINK_SERVICE}`, { params: query });
    return data;
  }

  @scCatchServiceException()
  static async getSmartLink(id: number, extraFields?: string[]): Promise<SmartLink> {
    const { data } = await axios.get<SmartLink>(`${SMART_LINK_SERVICE}/${id}`, {
      params: { extraFields },
    });
    return data;
  }

  @scCatchServiceException()
  static async getSmartLinkStats(
    id: number,
    reports: SmartLinkReportType[],
    startDate?: string,
    endDate?: string
  ): Promise<SmartLinkStats> {
    const { data } = await axios.get<SmartLinkStats>(`${SMART_LINK_SERVICE}/${id}/stats`, {
      params: { startDate, endDate, reports },
    });
    return data;
  }

  @scCatchServiceException()
  static async getSmartLinkStatsByPrompt(
    id: number | string,
    reports: SmartLinkReportType[],
    startDate?: string,
    endDate?: string,
    smartLinkId?: number
  ): Promise<SmartLinkStats> {
    let url = `${SMART_LINK_SERVICE}/stats?prompt_id=${id}`;
    if (smartLinkId) {
      url += `&smart_link_id=${smartLinkId}`;
    }
    const { data } = await axios.get<SmartLinkStats>(url, {
      params: { startDate, endDate, reports },
    });
    return data;
  }

  @scCatchServiceException()
  static async getSmartLinkStatsByCampaign(
    id: number | string,
    reports: SmartLinkReportType[],
    startDate?: string,
    endDate?: string
  ): Promise<SmartLinkStats> {
    const { data } = await axios.get<SmartLinkStats>(`${SMART_LINK_SERVICE}/stats?campaign_id=${id}`, {
      params: { startDate, endDate, reports },
    });
    return data;
  }

  @scCatchServiceException()
  static async createSmartLink(smartLink: Partial<SmartLink>): Promise<SmartLink> {
    const { data } = await axios.post<SmartLink>(`${SMART_LINK_SERVICE}`, {
      smart_link: smartLink,
    });
    return data;
  }

  @scCatchServiceException()
  static async updateSmartLink(id: number, smartLink: Partial<SmartLink>): Promise<SmartLink> {
    const { data } = await axios.patch<SmartLink>(`${SMART_LINK_SERVICE}/${id}`, {
      smart_link: smartLink,
    });
    return data;
  }

  @scCatchServiceException()
  static async getSmartLinkExtraParams(smartLinkId?: number): Promise<SmartLinkExtraParam[]> {
    const { data } = await axios.get<SmartLinkExtraParam[]>(
      `${SMART_LINK_PARAMS_SERVICE}${smartLinkId ? `?smart_link_id=${smartLinkId}` : ""}`
    );
    return data;
  }

  @scCatchServiceException()
  static async createSmartLinkExtraParam(request: SmartLinkExtraParam): Promise<SmartLinkExtraParam> {
    const { data } = await axios.post<SmartLinkExtraParam>(`${SMART_LINK_PARAMS_SERVICE}`, {
      smart_link_extra_param: request,
    });
    return data;
  }

  @scCatchServiceException()
  static async updateSmartLinkExtraParam(id: number, request: SmartLinkExtraParam): Promise<SmartLinkExtraParam> {
    const { data } = await axios.patch<SmartLinkExtraParam>(`${SMART_LINK_PARAMS_SERVICE}/${id}`, {
      smart_link_extra_param: request,
    });
    return data;
  }

  @scCatchServiceException()
  static async deleteSmartLinkExtraParam(id: number): Promise<void> {
    const { data } = await axios.delete(`${SMART_LINK_PARAMS_SERVICE}/${id}`);
    return data;
  }
}
